import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, CardActions, Grid, Typography, Button, Chip, Avatar,
  CircularProgress, TextField, InputAdornment, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, LinearProgress,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  Checkbox, FormControlLabel, FormGroup, Switch, Tooltip
} from '@mui/material';
import {
  School as TrainingIcon,
  Search as SearchIcon,
  YouTube as YouTubeIcon,
  Article as ArticleIcon,
  MenuBook as WikiIcon,
  Headphones as AudioIcon,
  Podcasts as PodcastIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  PlayCircle as VideoIcon,
  GitHub as GithubIcon,
  Collections as SlidesIcon,
  Quiz as FlashcardIcon,
  Timer as TimerIcon,
  CheckCircle as CompletedIcon,
  PlayArrow as StartIcon,
  Add as SubscribeIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

// Content type icons
const contentTypeIcons = {
  youtube: YouTubeIcon,
  wiki: WikiIcon,
  medium: ArticleIcon,
  document: ArticleIcon,
  url: LinkIcon,
  video: VideoIcon,
  pdf: PdfIcon,
  udemy: VideoIcon,
  coursera: VideoIcon,
  flashcard: FlashcardIcon,
  audio: AudioIcon,
  podcast: PodcastIcon,
  reference: LinkIcon,
  article: ArticleIcon,
  ebook: WikiIcon,
  slides: SlidesIcon,
  github: GithubIcon
};

// Difficulty colors
const difficultyColors = {
  beginner: 'success',
  intermediate: 'warning',
  advanced: 'error'
};

const CATEGORY_CARD_PALETTE = [
  { bg: '#E8F4FC', border: '#4A90C8', accent: '#0F4C75' },
  { bg: '#EEF8F1', border: '#3D9B6A', accent: '#1B5E3B' },
  { bg: '#FFF4E5', border: '#E6A023', accent: '#8A5A00' },
  { bg: '#F3E8FF', border: '#9B6BC7', accent: '#5B2D8E' },
  { bg: '#FFE8EE', border: '#D45B7A', accent: '#8B2040' },
  { bg: '#E6F7F5', border: '#2A9B91', accent: '#0D5C56' }
];

const TrainingCatalog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [catalogTab, setCatalogTab] = useState(0);
  const [selectedTrainingTitleIds, setSelectedTrainingTitleIds] = useState([]);

  const [availCategoryId, setAvailCategoryId] = useState('');
  const [availSpotId, setAvailSpotId] = useState('');
  const [availSpots, setAvailSpots] = useState([]);
  const [availCourses, setAvailCourses] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  /** When true, only categories that have at least one published course are listed (enrollable programs). */
  const [programsWithCoursesOnly, setProgramsWithCoursesOnly] = useState(false);
  const [addCoursesForCategory, setAddCoursesForCategory] = useState(null);
  const [addCourseTitleIds, setAddCourseTitleIds] = useState([]);
  const [addingCourses, setAddingCourses] = useState(false);

  const enrollableCategories = useMemo(
    () => (categories || []).filter((c) => !c.isStandaloneExamCatalog),
    [categories]
  );

  const fetchCatalog = useCallback(async () => {
    try {
      const response = await api.get('/training/catalog');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch catalog:', error);
      toast.error('Failed to load training catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    if (!availCategoryId) {
      setAvailSpots([]);
      return;
    }
    let cancelled = false;
    api
      .get('/training/learning-spots', { params: { category_id: availCategoryId } })
      .then((r) => {
        if (!cancelled) setAvailSpots(r.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setAvailSpots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [availCategoryId]);

  const loadAvailableCourses = useCallback(async () => {
    if (!availCategoryId) {
      setAvailCourses([]);
      return;
    }
    setLoadingAvail(true);
    try {
      const params = { category_id: availCategoryId };
      if (availSpotId) params.learning_spot_id = availSpotId;
      const r = await api.get('/training/catalog/available-courses', { params });
      setAvailCourses(r.data.data?.courses || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load available courses');
      setAvailCourses([]);
    } finally {
      setLoadingAvail(false);
    }
  }, [availCategoryId, availSpotId]);

  useEffect(() => {
    if (catalogTab === 3) {
      loadAvailableCourses();
    }
  }, [catalogTab, loadAvailableCourses]);

  useEffect(() => {
    if (!enrollableCategories.length) return;
    const valid = enrollableCategories.some((c) => c.id === availCategoryId);
    if (!availCategoryId || !valid) {
      setAvailCategoryId(enrollableCategories[0].id);
    }
  }, [enrollableCategories, availCategoryId]);

  const handleSubscribe = async () => {
    if (!selectedCategory) return;

    const published = selectedCategory.publishedCourses || [];
    if (published.length === 0) {
      toast.error('This program has no published courses yet. Publish a course under Sub-categories before enrolling.');
      return;
    }
    if (published.length > 1 && selectedTrainingTitleIds.length === 0) {
      toast.error('Select at least one course to enroll');
      return;
    }

    setEnrolling(true);
    try {
      const payload = { category_id: selectedCategory.id };
      if (published.length > 1) {
        payload.training_title_ids = selectedTrainingTitleIds;
      } else if (published.length === 1) {
        payload.training_title_ids = [published[0].id];
      }
      await api.post('/training/user/subscribe', payload);
      toast.success(`Enrolled in ${selectedCategory.name}!`);
      setSelectedCategory(null);
      setSelectedTrainingTitleIds([]);
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  const filteredCategories = categories.filter((cat) => {
    const q = search.toLowerCase();
    const matchesSearch =
      cat.name.toLowerCase().includes(q) || (cat.description && cat.description.toLowerCase().includes(q));
    if (!matchesSearch) return false;
    if (programsWithCoursesOnly) {
      if (cat.isStandaloneExamCatalog) return true;
      return (cat.publishedCourseCount ?? 0) > 0;
    }
    return true;
  });

  const tabFilteredCategories = filteredCategories.filter((cat) => {
    if (cat.isStandaloneExamCatalog && catalogTab !== 0 && catalogTab !== 3) {
      return false;
    }
    const en = cat.enrollment;
    switch (catalogTab) {
      case 0:
        return true;
      case 1:
        return en?.enrolled && en?.status !== 'completed';
      case 2:
        return en?.status === 'completed';
      default:
        return true;
    }
  });

  useEffect(() => {
    if (!selectedCategory) {
      setSelectedTrainingTitleIds([]);
      return;
    }
    const pub = selectedCategory.publishedCourses || [];
    if (pub.length === 1) {
      setSelectedTrainingTitleIds([pub[0].id]);
    } else {
      setSelectedTrainingTitleIds([]);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (!addCoursesForCategory) {
      setAddCourseTitleIds([]);
      return;
    }
    const selected = new Set(
      (addCoursesForCategory.enrollment?.selectedTrainingTitleIds || []).map((id) => String(id))
    );
    const notYet = (addCoursesForCategory.publishedCourses || []).filter((c) => !selected.has(String(c.id)));
    setAddCourseTitleIds(notYet.map((c) => c.id));
  }, [addCoursesForCategory]);

  const handleConfirmAddCourses = async () => {
    if (!addCoursesForCategory || addCourseTitleIds.length === 0) return;
    setAddingCourses(true);
    try {
      await api.post('/training/user/enrollment/add-courses', {
        category_id: addCoursesForCategory.id,
        training_title_ids: addCourseTitleIds
      });
      toast.success('New courses added to your training');
      setAddCoursesForCategory(null);
      fetchCatalog();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not add courses');
    } finally {
      setAddingCourses(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrainingIcon sx={{ fontSize: 32 }} />
          Training Catalog
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
          Open a <strong>training program</strong> to see category → sub-categories → <strong>published</strong> courses and outlines, then enroll. Only courses with status <strong>Published</strong> can be offered for enrollment.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={programsWithCoursesOnly}
                onChange={(e) => setProgramsWithCoursesOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Show only programs with published courses"
          />
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={catalogTab} onChange={(e, v) => setCatalogTab(v)}>
          <Tab label="All" />
          <Tab label="In progress" />
          <Tab label="Completed" />
          <Tab label="Currently available" />
        </Tabs>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        <strong>Why don’t my new courses show?</strong> Only courses with status <strong>Published</strong> appear here. In Training Administration, open Sub-categories
        and courses, edit the course, and set Status to <strong>published</strong>.
      </Alert>

      {catalogTab !== 3 && (
      <>
      {(catalogTab === 1 || catalogTab === 2) &&
        tabFilteredCategories.length === 0 &&
        filteredCategories.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" onClick={() => setCatalogTab(0)}>
              Show all programs
            </Button>
          }>
            <strong>In progress</strong> and <strong>Completed</strong> only list programs you are already enrolled in.
            Switch to the <strong>All</strong> tab to browse and enroll in new programs.
          </Alert>
        )}

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search categories or descriptions..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{ mb: 3 }}
      />

      {/* Categories Grid */}
      <Grid container spacing={3}>
        {tabFilteredCategories.map((category, idx) => {
          const isEnrolled = category.enrollment?.enrolled === true;
          const progress = category.enrollment?.progress || 0;
          const hasMoreCourses = category.enrollment?.hasMoreCoursesToAdd === true;
          const isCompleted =
            category.enrollment?.status === 'completed' && !hasMoreCourses;
          const palette = CATEGORY_CARD_PALETTE[idx % CATEGORY_CARD_PALETTE.length];
          const goCategory = () => {
            if (category.isStandaloneExamCatalog) {
              navigate('/training/standalone-exams');
              return;
            }
            navigate(`/training/catalog/category/${category.id}`);
          };

          const canEnroll =
            !category.isStandaloneExamCatalog &&
            (category.publishedCourseCount ?? 0) > 0 &&
            !isEnrolled;
          const enrollmentId = category.enrollment?.enrollmentId;
          const enrolledTitleIds = new Set(
            (category.enrollment?.selectedTrainingTitleIds || []).map((id) => String(id))
          );

          return (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card
                sx={{
                  height: '100%',
                  minHeight: 340,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: palette.bg,
                  border: '2px solid',
                  borderColor: isEnrolled ? 'primary.main' : palette.border,
                  borderRadius: 2,
                  boxShadow: '0 2px 10px rgba(15, 40, 60, 0.08)'
                }}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: palette.accent, width: 52, height: 52 }}>
                      <TrainingIcon sx={{ fontSize: 26 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: palette.accent, lineHeight: 1.2 }}>
                        {category.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Hover the preview box for editions
                      </Typography>
                    </Box>
                    {isEnrolled && (
                      <Chip size="small" color="success" label={isCompleted ? 'Completed' : `${progress}%`} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                    {category.isStandaloneExamCatalog ? (
                      <Chip
                        icon={<FlashcardIcon sx={{ fontSize: 14 }} />}
                        label={`${category.standaloneExamCount ?? 0} standalone exam(s)`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Chip label={`${category.materialCount ?? 0} materials`} size="small" variant="outlined" />
                        <Chip
                          label={`${category.publishedCourseCount ?? 0} courses`}
                          size="small"
                          variant="outlined"
                          color={(category.publishedCourseCount ?? 0) > 0 ? 'primary' : 'default'}
                        />
                        <Chip
                          icon={<TimerIcon sx={{ fontSize: 14 }} />}
                          label={formatDuration(category.totalDuration)}
                          size="small"
                          variant="outlined"
                        />
                      </>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 1.5
                    }}
                  >
                    {category.description || 'No description available'}
                  </Typography>

                  {/* Inner flip panel only — outer card stays put so Open / Enroll stay clickable */}
                  <Box
                    sx={{
                      mt: 'auto',
                      height: 142,
                      perspective: '900px',
                      '&:hover .catalog-editions-flip': { transform: 'rotateY(180deg)' }
                    }}
                  >
                    <Box
                      className="catalog-editions-flip"
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        transition: 'transform 0.5s',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          borderRadius: 1.5,
                          border: `1px dashed ${palette.border}`,
                          bgcolor: '#fff',
                          p: 1.25,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: palette.accent }}>
                          Published editions preview
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {(category.publishedCourses || []).length > 0
                            ? `${category.publishedCourses.length} course(s) — hover for enrolled / not enrolled`
                            : category.isStandaloneExamCatalog
                              ? 'Standalone exam catalog'
                              : 'No published courses yet'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                          borderRadius: 1.5,
                          border: `1px solid ${palette.border}`,
                          bgcolor: '#fff',
                          p: 1,
                          overflow: 'auto'
                        }}
                      >
                        {(category.publishedCourses || []).length > 0 ? (
                          (category.publishedCourses || []).slice(0, 5).map((c) => {
                            const courseCompleted = !!c.completed || c.learnerStatus === 'completed';
                            const courseEnrolled =
                              courseCompleted ||
                              !!c.enrolled ||
                              c.learnerStatus === 'enrolled' ||
                              enrolledTitleIds.has(String(c.id));
                            const statusLabel = courseCompleted
                              ? 'Completed'
                              : courseEnrolled
                                ? 'Already enrolled'
                                : 'Not enrolled';
                            return (
                              <Box
                                key={c.id}
                                sx={{
                                  mb: 0.75,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  gap: 0.5
                                }}
                              >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}
                                  >
                                    {c.name}
                                    {c.edition_label ? ` (${c.edition_label})` : ''}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 0.25 }}>
                                    {c.course_code && (
                                      <Chip
                                        size="small"
                                        label={c.course_code}
                                        sx={{ fontFamily: 'monospace', height: 18, fontSize: '0.6rem' }}
                                      />
                                    )}
                                    <Chip
                                      size="small"
                                      label={c.delivery_type === 'ILT' ? 'ILT' : 'SPT'}
                                      sx={{ height: 18, fontSize: '0.6rem' }}
                                    />
                                  </Box>
                                </Box>
                                <Chip
                                  size="small"
                                  color={courseCompleted ? 'success' : courseEnrolled ? 'primary' : 'default'}
                                  variant={courseCompleted || courseEnrolled ? 'filled' : 'outlined'}
                                  label={statusLabel}
                                  sx={{ height: 20, fontSize: '0.6rem', flexShrink: 0 }}
                                />
                              </Box>
                            );
                          })
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Nothing published in this program yet.
                          </Typography>
                        )}
                        {(category.publishedCourses || []).length > 5 && (
                          <Typography variant="caption" color="text.secondary">
                            +{category.publishedCourses.length - 5} more in Open catalog
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 1.5, pt: 0, flexWrap: 'wrap', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={goCategory}
                    sx={{ bgcolor: palette.accent, '&:hover': { bgcolor: palette.accent } }}
                  >
                    Open catalog
                  </Button>
                  {isEnrolled && enrollmentId && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<StartIcon />}
                      onClick={() => navigate(`/training/learn/${enrollmentId}`)}
                    >
                      {isCompleted ? 'Review' : 'Start attending'}
                    </Button>
                  )}
                  {canEnroll && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SubscribeIcon />}
                      onClick={() => setSelectedCategory(category)}
                    >
                      Enroll
                    </Button>
                  )}
                  {hasMoreCourses && (
                    <Button size="small" variant="text" onClick={() => setAddCoursesForCategory(category)}>
                      Add more courses
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}

        {tabFilteredCategories.length === 0 && (
          <Grid item xs={12}>
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No training programs found in this tab (or matching your search).
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
      </>
      )}

      {catalogTab === 3 && (
        <Card sx={{ p: 2, mb: 3 }} variant="outlined">
          <Alert severity="info" sx={{ mb: 2 }}>
            To <strong>enroll</strong>, use <strong>Enroll</strong> on a card (All tab), <strong>Enroll in program</strong> below, or <strong>View program</strong> → <strong>Enroll now</strong>. After enrolling, use <strong>Start attending</strong> / My Training → Continue Learning.
          </Alert>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Published courses (filter by program and sub-category)
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={availCategoryId}
                  onChange={(e) => {
                    setAvailCategoryId(e.target.value);
                    setAvailSpotId('');
                  }}
                >
                  {enrollableCategories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={!availCategoryId}>
                <InputLabel>Sub-category</InputLabel>
                <Select
                  label="Sub-category"
                  value={availSpotId}
                  onChange={(e) => setAvailSpotId(e.target.value)}
                >
                  <MenuItem value="">All sub-categories</MenuItem>
                  {availSpots.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {loadingAvail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Course</TableCell>
                    <TableCell>Sub-category</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Your status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availCourses.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.subCategory?.name || '—'}</TableCell>
                      <TableCell>{row.delivery_type === 'ILT' ? 'ILT' : 'SPT'}</TableCell>
                      <TableCell>
                        {row.completed ? (
                          <Chip size="small" label="Completed" color="success" />
                        ) : (
                          <Chip
                            size="small"
                            label={
                              row.courseProgress === 'not_enrolled'
                                ? 'Not enrolled'
                                : row.courseProgress === 'completed'
                                  ? 'Completed'
                                  : row.courseProgress === 'in_progress'
                                    ? 'In progress'
                                    : row.courseProgress || '—'
                            }
                            color={row.courseProgress === 'in_progress' ? 'primary' : 'default'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => navigate(`/training/catalog/category/${availCategoryId}`)}
                        >
                          View program
                        </Button>
                        {row.courseProgress === 'not_enrolled' && (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ ml: 0.5 }}
                            onClick={() => {
                              const cat = categories.find((c) => c.id === availCategoryId);
                              if (cat) {
                                setSelectedCategory({
                                  ...cat,
                                  publishedCourses: (cat.publishedCourses || []).filter((c) => c.id === row.id)
                                });
                                setSelectedTrainingTitleIds([row.id]);
                              } else {
                                navigate(`/training/catalog/category/${availCategoryId}`);
                              }
                            }}
                          >
                            Enroll
                          </Button>
                        )}
                        {(row.courseProgress === 'in_progress' || row.courseProgress === 'completed') && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                            onClick={() => {
                              const cat = categories.find((c) => c.id === availCategoryId);
                              const eid = cat?.enrollment?.enrollmentId;
                              if (eid) navigate(`/training/learn/${eid}`);
                              else navigate('/training/my-training');
                            }}
                          >
                            {row.courseProgress === 'completed' ? 'Review' : 'Continue'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {availCourses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No published courses for this filter.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {/* Add new courses to existing enrollment */}
      <Dialog
        open={!!addCoursesForCategory}
        onClose={() => !addingCourses && setAddCoursesForCategory(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add courses to your training</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select published courses that were added after you enrolled. Your program enrollment stays the same; we add the new course outlines and materials to your training.
          </Alert>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {addCoursesForCategory?.name}
          </Typography>
          <FormGroup>
            {(addCoursesForCategory?.publishedCourses || [])
              .filter((c) => {
                const taken = (addCoursesForCategory?.enrollment?.selectedTrainingTitleIds || []).map(String);
                return !taken.includes(String(c.id));
              })
              .map((c) => (
                <FormControlLabel
                  key={c.id}
                  control={
                    <Checkbox
                      checked={addCourseTitleIds.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAddCourseTitleIds((prev) => [...prev, c.id]);
                        } else {
                          setAddCourseTitleIds((prev) => prev.filter((id) => id !== c.id));
                        }
                      }}
                    />
                  }
                  label={`${c.name} (${c.delivery_type === 'ILT' ? 'ILT' : 'SPT'}) · ${c.subCategoryName || ''}`}
                />
              ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddCoursesForCategory(null)} disabled={addingCourses}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAddCourses}
            disabled={addingCourses || addCourseTitleIds.length === 0}
            startIcon={addingCourses ? <CircularProgress size={16} /> : <SubscribeIcon />}
          >
            {addingCourses ? 'Adding…' : 'Add to my training'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enroll Confirmation Dialog */}
      <Dialog 
        open={!!selectedCategory} 
        onClose={() => setSelectedCategory(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: selectedCategory?.color || '#6366f1' }}>
            <TrainingIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">Enroll in Training</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedCategory?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            When a program has several published courses, choose which course(s) you are enrolling for. Progress is tracked for materials in those course outlines.
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedCategory?.description}
          </Typography>

          {(selectedCategory?.publishedCourses || []).length > 1 && (
            <FormGroup sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select one or more courses
              </Typography>
              {(selectedCategory.publishedCourses || []).map((c) => (
                <FormControlLabel
                  key={c.id}
                  control={
                    <Checkbox
                      checked={selectedTrainingTitleIds.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTrainingTitleIds((prev) => [...prev, c.id]);
                        } else {
                          setSelectedTrainingTitleIds((prev) => prev.filter((id) => id !== c.id));
                        }
                      }}
                    />
                  }
                  label={`${c.name} (${c.delivery_type === 'ILT' ? 'ILT' : 'SPT'}) · ${c.subCategoryName || ''}`}
                />
              ))}
            </FormGroup>
          )}

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {selectedCategory?.materialCount || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Training Items
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {formatDuration(selectedCategory?.totalDuration)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Duration
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setSelectedCategory(null); setSelectedTrainingTitleIds([]); }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubscribe}
            disabled={
              enrolling ||
              ((selectedCategory?.publishedCourses || []).length === 0 &&
                !selectedCategory?.isStandaloneExamCatalog)
            }
            startIcon={enrolling ? <CircularProgress size={16} /> : <SubscribeIcon />}
          >
            {enrolling ? 'Enrolling...' : 'Confirm Enrollment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingCatalog;
